/**
 * PoseOverlay Component
 *
 * Draws MediaPipe pose landmarks (skeleton) on a canvas overlay.
 * Provides visual feedback showing detected body keypoints and connections.
 */

import React, { useEffect, useRef } from 'react';
import { NormalizedLandmark } from '@mediapipe/tasks-vision';
import { debugLog } from '../../../../../common/debugUtilities';

interface PoseOverlayProps {
  landmarks: NormalizedLandmark[] | null;
  width: number;
  height: number;
  showSkeleton?: boolean;
  showKeypoints?: boolean;
}

// MediaPipe Pose landmark connections (33 keypoints)
const POSE_CONNECTIONS = [
  // Face
  [0, 1], [1, 2], [2, 3], [3, 7], [0, 4], [4, 5], [5, 6], [6, 8],
  // Torso
  [9, 10], [11, 12], [11, 13], [13, 15], [12, 14], [14, 16],
  [11, 23], [12, 24], [23, 24],
  // Left arm
  [11, 13], [13, 15], [15, 17], [15, 19], [15, 21], [17, 19],
  // Right arm
  [12, 14], [14, 16], [16, 18], [16, 20], [16, 22], [18, 20],
  // Left leg
  [23, 25], [25, 27], [27, 29], [27, 31], [29, 31],
  // Right leg
  [24, 26], [26, 28], [28, 30], [28, 32], [30, 32]
];

export default function PoseOverlay({
  landmarks,
  width,
  height,
  showSkeleton = true,
  showKeypoints = true
}: PoseOverlayProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      debugLog('[PoseOverlay] No canvas ref');
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      debugLog('[PoseOverlay] No canvas context');
      return;
    }

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    if (!landmarks || landmarks.length === 0) {
      debugLog('[PoseOverlay] No landmarks to draw');
      return;
    }

    debugLog('[PoseOverlay] Drawing skeleton:', {
      width,
      height,
      landmarkCount: landmarks.length
    });

    // Draw connections (skeleton)
    if (showSkeleton) {
      ctx.strokeStyle = '#00FF00'; // Green
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';

      POSE_CONNECTIONS.forEach(([startIdx, endIdx]) => {
        const start = landmarks[startIdx];
        const end = landmarks[endIdx];

        if (!start || !end) return;

        // Only draw if both points are visible
        const minVisibility = 0.5;
        if (start.visibility && end.visibility &&
            start.visibility > minVisibility && end.visibility > minVisibility) {
          ctx.beginPath();
          ctx.moveTo(start.x * width, start.y * height);
          ctx.lineTo(end.x * width, end.y * height);
          ctx.stroke();
        }
      });
    }

    // Draw keypoints
    if (showKeypoints) {
      landmarks.forEach((landmark, index) => {
        if (!landmark.visibility || landmark.visibility < 0.5) return;

        const x = landmark.x * width;
        const y = landmark.y * height;

        // Different colors for different body parts
        let color = '#FFFFFF'; // White for general points
        if (index <= 10) color = '#FF0000'; // Red for face/head
        else if (index >= 11 && index <= 16) color = '#00FF00'; // Green for arms
        else if (index >= 23 && index <= 32) color = '#0000FF'; // Blue for legs
        else color = '#FFFF00'; // Yellow for torso

        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, 2 * Math.PI);
        ctx.fill();

        // Add white border
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 1;
        ctx.stroke();
      });
    }
  }, [landmarks, width, height, showSkeleton, showKeypoints]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10
        // No mirroring - matches natural back camera view
      }}
    />
  );
}
